import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import LoadingPng from '../../assets/images/Loading.png';

import UserTabs from './UserTabs';
import UserHeader from './UserHeader';

import { userApi } from '../../api/user';
import { useAuth } from '../../context/AuthContext';
import { settingsApi } from '../../api/settings';

const formatRole = (roleName) => {
  console.log('rolename', roleName);
  return roleName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

export default function User() {
  const { tokens, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10); // Reduced for better pagination experience
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [roleId, setRoleId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('roleId') || '';
  });
  const [loading, setLoading] = useState(false);

  const tabsData = [
    { title: 'All', role: '' },
    { title: 'Admin', role: 1 },
    { title: 'Incorporator', role: 2 },
    { title: 'Sub Operator', role: 3 },
    { title: 'General Coordinator', role: 4 },
    { title: 'Coordinator', role: 5 },
    { title: 'Sub Coordinator', role: 6 },
    { title: 'Agent', role: 7 },
    { title: 'Sub Agent', role: 8 },
    { title: 'Player', role: 9 },
  ];

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Pass search query directly as a query parameter, not as part of a string
      const response = await userApi.getUsers(
        tokens.access.token,
        page,
        limit,
        roleId,
        searchQuery // Changed from `search=${searchQuery}` to just searchQuery
      );

      if (response.status === 200) {
        // Filter out users with roles higher than or equal to current user's role
        const filteredUsers = response.data.rows
          .map((user) => ({
            id: user.id.toString(),
            name: user.name,
            role: {
              id: user.Role?.id,
              name: user.Role?.name,
            },
            email: user.email,
            phone: user.mobileNumber,
            walletBalance: user.Wallet?.balance || 0,
            commission: parseFloat(user.commissions),
            isActive: user.active,
            showWin:
              user.Settings?.find((setting) => setting.key === 'showWin')
                ?.value === 'true' || false,
          }))
          .filter((userData) => {
            // If "All" is selected, still only show users with roles below current user
            if (roleId === '') {
              return userData.role.id > user.roleId;
            }
            // Otherwise, filter by selected role ID, but still exclude users with roles higher or equal to current user
            return (
              userData.role.id > user.roleId &&
              (roleId === '' ||
                userData.role.id.toString() === roleId.toString())
            );
          });

        setUsers(filteredUsers);
        // Update pagination based on server response instead of filtered results
        setTotalCount(response.data.count);
        setTotalPages(Math.ceil(response.data.count / limit));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [tokens.access.token, searchQuery, roleId, page, limit]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setPage(1); // Reset to first page when searching

    // Update URL with search query
    const params = new URLSearchParams(window.location.search);
    params.set('page', '1');
    if (query) {
      params.set('search', query);
    } else {
      params.delete('search');
    }

    // Update URL without reloading the page
    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${params.toString()}`
    );
  };

  const handleRoleChange = (newRoleId) => {
    setRoleId(newRoleId);
    setPage(1); // Reset to first page

    // Update URL with new roleId
    const params = new URLSearchParams(window.location.search);
    params.set('page', '1');
    params.set('limit', limit.toString());
    if (newRoleId) {
      params.set('roleId', newRoleId.toString());
    } else {
      params.delete('roleId');
    }
    // Preserve search query in URL
    if (searchQuery) {
      params.set('search', searchQuery);
    }

    // Update URL without reloading the page
    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${params.toString()}`
    );
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);

    // Update URL with new page
    const params = new URLSearchParams(window.location.search);
    params.set('page', newPage.toString());

    // Update URL without reloading the page
    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${params.toString()}`
    );
  };

  const handleUserUpdate = (updatedUser) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === updatedUser.id
          ? { ...user, isActive: updatedUser.isActive }
          : user
      )
    );
  };

  const handleShowWinUpdate = async (userId, showWin) => {
    try {
      const response = await settingsApi.settings(
        tokens.access.token,
        userId,
        'showWin',
        showWin.toString()
      );

      if (response.status === 200) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId ? { ...user, showWin } : user
          )
        );
        toast.success('Show Win setting updated');
      }
    } catch (error) {
      console.error('Error updating showWin:', error);
      toast.error('Failed to update Show Win setting');
    }
  };

  return (
    <div className='min-h-screen relative overflow-hidden font-poppins'>
      {loading && (
        <div className='absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50'>
          <img
            src={LoadingPng}
            alt='Loading'
            className='w-16 h-16 animate-spin'
          />
        </div>
      )}
      <HeaderWithIcon title='Users' address='/account' />
      <UserHeader onSearch={handleSearch} />

      <UserTabs
        tabsData={tabsData}
        users={users}
        onRoleChange={handleRoleChange}
        selectedRole={roleId}
        user={user}
        onUserUpdate={handleUserUpdate}
        onToggleShowWin={handleShowWinUpdate}
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        loading={loading}
      />
    </div>
  );
}
